import React, { useState, useRef } from "react";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { Button } from "@progress/kendo-react-buttons";
import Scrollable from "../Shared/Edit/Scrollable";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import AttributeTypes from "./AttributeTypes";
import ErrorMessage from "../Shared/Error/ErrorMessage";
import { OverlayLoader } from "../Shared/Loader/OverlayLoader";
import InnerLoader from "../Shared/Loader/InnerLoader";
import { useTranslation } from "react-i18next";
import { createSharedLookup } from "../../API/SharedLookup/SharedLookup";

const CreateSharedPropertyDialog = ({ handleClose, sharedProperties, updatedSharedProperties, projectId }) => {

    const sharedPropertyNameInputRef = useRef(null);

    const { t, ready: tReady } = useTranslation([
        "attribute/shared_property",
    ]);

    const sharedPropertyTypes = AttributeTypes.Types.filter((item) => item.value === "Picklist String" || item.value === "Tree Dropdown");

    const [formFields, setFormFields] = useState({
        name: "",
        type: "Shared Lookup",
        sharedLookupType: sharedPropertyTypes[0],
    });

    const [validName, setValidName] = useState({
        isValid: true,
        errorMessage: ""
    });

    const [creatingSharedProperty, setCreatingSharedProperty] = useState(false);

    const handleNameChange = (e) => {
        const name = e.target.value.trim();
        const nameIsReserved = name.toLowerCase() === "id";
        const nameIsBlank = name === "";
        const nameInUse = sharedProperties.some(item => item.name.trim().toLowerCase() === name.toLowerCase());
        const nameContainsSys = /^\[SYS\]/i.test(name);
        const nameStartWithNumber = /^\d/.test(name);
        const isNameLong = name.length > 30;

        const validName = {
            isValid: !(nameIsBlank || nameStartWithNumber || nameInUse || isNameLong || nameContainsSys || nameIsReserved),
            errorMessage: ""
        }

        if (!validName.isValid) {
            if (nameIsBlank) {
                validName.errorMessage = t("create_dialog.errors.name_required")
            } else if (nameStartWithNumber) {
                validName.errorMessage = t("create_dialog.errors.name_starts_with_number");
            } else if (nameInUse) {
                validName.errorMessage = t("create_dialog.errors.name_in_use");
            } else if (isNameLong) {
                validName.errorMessage = t("create_dialog.errors.name_too_long");
            } else if (nameContainsSys) {
                validName.errorMessage = t("create_dialog.errors.name_contains_sys");
            } else if (nameIsReserved) {
                validName.errorMessage = t("create_dialog.errors.name_is_reserved");
            } else {
                validName.errorMessage = "";
            }
        }
        setValidName(validName);
        setFormFields({ ...formFields, name: e.target.value });
    }

    const handleSave = async (createNew) => {
        try {
            setCreatingSharedProperty(true);
            const sharedPropertyResponse = await createSharedLookup(projectId, {
                name: formFields.name,
                type: formFields.type,
                sysName: "sharedlookup",
                sharedLookupType: formFields.sharedLookupType.value,
            });
            updatedSharedProperties([...sharedProperties, sharedPropertyResponse.data]);
            if (createNew) {
                setFormFields({
                    name: "",
                    type: "Shared Lookup",
                    sharedLookupType: sharedPropertyTypes[0],
                });
                sharedPropertyNameInputRef.current.focus();
            } else {
                handleClose(true);
            }
        } catch (error) {
            console.error("Error occurred:", error);
        } finally {
            setCreatingSharedProperty(false); // Resetting loading state
        }
    }

    return (
        <Dialog
            title={t("create_dialog.title")}
            onClose={() => handleClose(false)}
            width="30%"
            height="50%"
        >
            {creatingSharedProperty && <OverlayLoader />}
            <Scrollable>
                <div className="mb-2">
                    <div className="me-1 form-label d-inline-block" required>
                        {t("create_dialog.name_label")}
                    </div>
                    {!validName.isValid &&
                        <ErrorMessage
                            valid={validName.isValid}
                            error={validName.errorMessage}
                        />
                    }
                    <input
                        maxLength={64}
                        onChange={handleNameChange}
                        className="form-control name-text"
                        autoFocus={true}
                        ref={sharedPropertyNameInputRef}
                        type="text"
                        value={formFields.name}
                    />
                </div>
                <div>
                    <div className="me-1 form-label d-inline-block">
                        {t("create_dialog.shared_property_type_label")}
                    </div>
                    <DropDownList
                        data={sharedPropertyTypes}
                        value={formFields.sharedLookupType}
                        onChange={(e) => setFormFields({ ...formFields, sharedLookupType: e.target.value })}
                        textField="text"
                    />
                </div>
            </Scrollable>
            <DialogActionsBar layout="end">
                <div className="m-3">
                    {creatingSharedProperty && (
                        <small className="mt-2">
                            <div className="d-inline-block">
                                <InnerLoader borderColor="green" />
                                <span className="me-4 text-danger"></span>
                            </div>
                        </small>
                    )}
                    <Button
                        onClick={() => handleClose(false)}
                        className="btn btn-secondary"
                    >
                        {t("create_dialog.buttons.cancel_button_label")}
                    </Button>
                    <Button
                        disabled={!validName.isValid || formFields.name === ""}
                        onClick={() => handleSave(true)}
                        className="btn btn-primary"
                    >
                        {t("create_dialog.buttons.create_and_new_button_label")}
                    </Button>
                    <Button
                        disabled={!validName.isValid || formFields.name === ""}
                        onClick={() => handleSave(false)}
                        className="btn btn-primary"
                    >
                        {t("create_dialog.buttons.create_button_label")}
                    </Button>
                </div>
            </DialogActionsBar>
        </Dialog >
    );
}
export default CreateSharedPropertyDialog;